import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, Loader2, PackageCheck, Handshake, FileText, ShieldAlert } from 'lucide-react';
import { api } from '@/api/client';
import {
  AddressBlock, AgeBadge, StatusChip, fmtDate, fmtDateTime,
  RECEIVABLE_STATUS_IDS, RECEIVED_STATUS_ID, type RtoMeta, type RtoRow,
} from './rtoShared';
import { ReceiveDialog, HandoverDialog } from './RtoActionDialogs';

interface TimelineEvent {
  id: string; statusId: number; status: string; message?: string | null; at: string; location?: string | null;
  courier?: string | null; updatedBy?: string | null; source?: string | null; remarks?: string | null;
  warehouse?: string | null; override?: boolean; proofUrl?: string | null; isRtoModuleEvent: boolean;
}
interface RtoDetail extends RtoRow {
  products?: any; dimensions?: any; chargeableWeight?: any; totalBoxes?: number | null;
  rtoReason?: string | null; lastRtoEvent?: { status: string; at: string; message?: string | null } | null;
  proofUrl?: string | null; timeline: TimelineEvent[];
}

function Field({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-[11px] uppercase text-slate-500">{k}</div>
      <div className="text-sm text-slate-900 break-words">{v ?? '—'}</div>
    </div>
  );
}

export function RtoDetailDrawer({ id, meta, onClose, onChanged }: { id: string | null; meta?: RtoMeta; onClose: () => void; onChanged: () => void }) {
  const [showReceive, setShowReceive] = useState(false);
  const [showHandover, setShowHandover] = useState(false);
  const { data, isLoading, refetch, error } = useQuery<RtoDetail>({
    queryKey: ['rto-detail', id],
    enabled: !!id,
    queryFn: async () => (await api.get(`/orders/rto/${id}`)).data,
  });
  if (!id) return null;

  const canReceive = !!meta?.permissions.receive && !!data && RECEIVABLE_STATUS_IDS.includes(data.statusId) && !data.rtoReceivedAt;
  const canHandover = !!meta?.permissions.handover && !!data && data.statusId === RECEIVED_STATUS_ID;
  const changed = () => { refetch(); onChanged(); };
  const products: any[] = Array.isArray(data?.products) ? data!.products : [];

  return (
    <div className="fixed inset-0 z-[1100] flex">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-full max-w-3xl bg-white h-full overflow-y-auto shadow-xl">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center gap-3 z-10">
          <div className="min-w-0">
            <div className="text-xs text-slate-500">RTO order</div>
            <div className="font-semibold text-slate-900 truncate">{data?.orderNo ?? '…'} <span className="font-mono text-slate-500 text-sm">{data?.awb}</span></div>
          </div>
          {data && <StatusChip id={data.statusId} name={data.statusName} />}
          <div className="ml-auto flex items-center gap-2">
            {canReceive && (
              <button onClick={() => setShowReceive(true)} className="px-3 py-1.5 bg-primary text-white rounded-lg text-xs font-medium flex items-center gap-1.5">
                <PackageCheck size={14} /> I Received This Order
              </button>
            )}
            {canHandover && (
              <button onClick={() => setShowHandover(true)} className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-medium flex items-center gap-1.5">
                <Handshake size={14} /> Deliver to Merchant
              </button>
            )}
            <button onClick={onClose} className="p-1.5 text-slate-500 hover:text-slate-900"><X size={18} /></button>
          </div>
        </div>

        {isLoading && <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div>}
        {error && <div className="p-5 text-sm text-red-600">{(error as any)?.response?.data?.message ?? 'Could not load this RTO order.'}</div>}

        {data && (
          <div className="p-5 space-y-5">
            {data.courierReportedOnly && (
              <div className="flex items-start gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2">
                <ShieldAlert size={14} className="mt-0.5 shrink-0" />
                The courier reports this return as delivered, but it has not been received at a GT warehouse yet.
              </div>
            )}

            <section>
              <h3 className="text-xs font-semibold uppercase text-slate-500 mb-2">Order information</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Field k="Order ID" v={data.orderNo} />
                <Field k={data.orderType === 'B2B' ? 'LR / AWB' : 'AWB'} v={<span className="font-mono">{data.awb}</span>} />
                <Field k="Order date" v={fmtDate(data.orderDate)} />
                <Field k="Order type" v={data.orderType} />
                {!meta?.isMerchant && <Field k="Merchant" v={data.merchant} />}
                <Field k="Courier" v={data.courier} />
                <Field k="Shipment value" v={data.shipmentValue != null ? `${String.fromCharCode(8377)}${Number(data.shipmentValue).toLocaleString('en-IN')}` : '—'} />
                <Field k="Payment type" v={data.paymentType} />
                <Field k="Package" v={[
                  data.totalBoxes ? `${data.totalBoxes} box(es)` : null,
                  data.chargeableWeight ? `${Number(data.chargeableWeight)} kg` : null,
                  products.length ? products.map((p) => `${p?.name ?? 'Item'}${p?.qty ? ` x${p.qty}` : ''}`).join(', ') : null,
                ].filter(Boolean).join(' · ') || '—'} />
              </div>
            </section>

            <section className="grid md:grid-cols-2 gap-3">
              <AddressBlock title="From (sender)" a={data.from} />
              <AddressBlock title="To (customer)" a={data.to} />
              {data.returnTo && <AddressBlock title="Return address on the booking" a={data.returnTo} />}
            </section>

            <section>
              <h3 className="text-xs font-semibold uppercase text-slate-500 mb-2">RTO information</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Field k="RTO initiated" v={fmtDateTime(data.rtoInitiatedAt)} />
                <Field k="RTO status" v={data.statusName} />
                <Field k="RTO reason" v={data.rtoReason} />
                <Field k="Last RTO event" v={data.lastRtoEvent ? `${data.lastRtoEvent.status} · ${fmtDateTime(data.lastRtoEvent.at)}` : '—'} />
                <Field k="RTO received" v={data.rtoReceivedAt ? `${fmtDateTime(data.rtoReceivedAt)} · ${data.rtoReceivedBranch ?? ''}` : '—'} />
                <Field k="Received by" v={data.rtoReceivedBy} />
                <Field k="Merchant return" v={data.merchantReturnDate ? fmtDateTime(data.merchantReturnDate) : '—'} />
                <Field k="Returned by" v={data.merchantReturnBy} />
                <Field k="RTO ageing" v={<AgeBadge days={data.rtoAgeingDays} open={data.isOpen} />} />
                <Field k="Warehouse ageing" v={<AgeBadge days={data.warehouseAgeingDays} open={data.isOpen} />} />
                {data.redispatchedTo && (
                  <Field k="Re-dispatched as" v={`${data.redispatchedTo.orderNo}${data.redispatchedTo.awb ? ` · ${data.redispatchedTo.awb}` : ''}`} />
                )}
                <Field k="Handover proof" v={data.proofUrl
                  ? <a href={data.proofUrl} target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-1"><FileText size={13} /> View proof</a>
                  : '—'} />
              </div>
            </section>

            <section>
              <h3 className="text-xs font-semibold uppercase text-slate-500 mb-3">Tracking & event timeline</h3>
              <ol className="relative border-l border-slate-200 ml-2 space-y-4">
                {data.timeline.map((e) => (
                  <li key={e.id} className="ml-4">
                    <span className={`absolute -left-1.5 mt-1.5 w-3 h-3 rounded-full border-2 border-white ${e.isRtoModuleEvent ? 'bg-indigo-500' : 'bg-slate-300'}`} />
                    <div className="flex flex-wrap items-center gap-2">
                      <StatusChip id={e.statusId} name={e.status} />
                      <span className="text-xs text-slate-500">{fmtDateTime(e.at)}</span>
                      {e.override && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-200">Override</span>}
                    </div>
                    {e.message && <div className="text-sm text-slate-800 mt-0.5">{e.message}</div>}
                    <div className="text-[11px] text-slate-500 mt-0.5 flex flex-wrap gap-x-3">
                      {(e.warehouse || e.location) && <span>Location: {e.warehouse ?? e.location}</span>}
                      {e.courier && <span>Courier: {e.courier}</span>}
                      <span>Updated by: {e.updatedBy ?? 'System'}</span>
                      {e.source && <span>Source: {e.source}</span>}
                      {e.remarks && <span>Remarks: {e.remarks}</span>}
                      {e.proofUrl && <a href={e.proofUrl} target="_blank" rel="noreferrer" className="text-primary hover:underline">Proof</a>}
                    </div>
                  </li>
                ))}
                {!data.timeline.length && <li className="ml-4 text-sm text-slate-400">No tracking events recorded.</li>}
              </ol>
            </section>
          </div>
        )}
      </div>

      <ReceiveDialog open={showReceive} onClose={() => setShowReceive(false)} meta={meta}
        order={data ? { id: data.id, awb: data.awb, statusName: data.statusName } : null} onDone={changed} />
      <HandoverDialog open={showHandover} onClose={() => setShowHandover(false)}
        order={data ? { id: data.id, awb: data.awb, orderType: data.orderType, merchant: data.merchant } : null} onDone={changed} />
    </div>
  );
}
